/**
 * Tradable market universe for Oroboros — HL tape (core + HIP-3) with Grail filter.
 */

import { oroborosJson } from './client';
import {
  fetchOroUniverseTape,
  type OroTapeKind,
  type OroTapeRow,
} from './market';
import { pairToHlCoin, pairToPerpLabel } from './portfolio';

export type OroMarket = OroTapeRow;

const FALLBACK_SYMBOLS = ['BTC', 'ETH', 'SOL', 'ARB', 'AVAX', 'LINK', 'DOGE', 'OP', 'UNI'];

const PARAGON_COINS = new Set(['TOTAL2', 'OTHERS', 'BTCD']);

const HIP3_QUOTES = new Set(['USDC', 'USDT', 'USDT0', 'USDH', 'USDE']);

function quoteToDex(base: string, quote: string): string {
  const q = quote.toUpperCase();
  const b = base.toUpperCase();
  if (q === 'USDT' || q === 'USDT0') return 'cash';
  if (q === 'USDH' || q === 'USDE') return 'km';
  if (q === 'USDC') return PARAGON_COINS.has(b) ? 'para' : 'xyz';
  return 'xyz';
}

/** Normalize API / HL symbols into a stable market id used by the terminal. */
export function symbolToMarketId(raw: string): string {
  const s = String(raw || '').trim();
  if (!s) return 'BTC-PERP';
  if (s.includes(':')) return s;
  const u = s.toUpperCase();
  const dash = u.lastIndexOf('-');
  if (dash > 0) {
    const base = u.slice(0, dash);
    const quote = u.slice(dash + 1);
    if (HIP3_QUOTES.has(quote)) {
      const dex = quoteToDex(base, quote);
      return `${dex}:${base}`;
    }
    if (quote === 'USD' || quote === 'PERP') {
      return pairToPerpLabel(base);
    }
  }
  return pairToPerpLabel(pairToHlCoin(s));
}

export function marketIdToCoin(marketId: string): string {
  const id = String(marketId || '').trim();
  if (id.includes(':')) return id;
  return pairToHlCoin(id);
}

export function marketKind(marketId: string): OroTapeKind {
  const id = String(marketId || '');
  if (id.includes(':')) return 'hip3';
  const u = id.toUpperCase();
  if (HIP3_QUOTES.has(u.split('-').pop() || '')) return 'hip3';
  return 'perp';
}

function parsePairsPayload(data: unknown): string[] {
  if (Array.isArray(data)) {
    return data.map((x) => String(x || '').trim()).filter(Boolean);
  }
  if (data && typeof data === 'object' && Array.isArray((data as any).pairs)) {
    return (data as any).pairs.map((x: unknown) => String(x || '').trim()).filter(Boolean);
  }
  return [];
}

const ALLOW_TTL_MS = 5 * 60_000;
let allowCache: { includeHip3: boolean; ts: number; symbols: string[]; set: Set<string> } | null =
  null;
let allowInFlight: { includeHip3: boolean; p: Promise<Set<string> | null> } | null = null;

function allowSetFromSymbols(symbols: string[]): Set<string> {
  const set = new Set<string>();
  for (const s of symbols) {
    set.add(symbolToMarketId(s).toUpperCase());
    set.add(pairToHlCoin(s).toUpperCase());
    set.add(String(s).toUpperCase());
  }
  return set;
}

/** Raw Grail allowlist symbols (cached). Used by the market picker and strategy pair dropdown. */
export async function fetchOroborosPairAllowlist(includeHip3: boolean): Promise<string[]> {
  if (
    allowCache &&
    allowCache.includeHip3 === includeHip3 &&
    Date.now() - allowCache.ts < ALLOW_TTL_MS
  ) {
    return allowCache.symbols;
  }
  const set = await fetchGrailAllowlist(includeHip3);
  return set && allowCache && allowCache.includeHip3 === includeHip3 ? allowCache.symbols : [];
}

async function fetchGrailAllowlist(includeHip3: boolean): Promise<Set<string> | null> {
  if (
    allowCache &&
    allowCache.includeHip3 === includeHip3 &&
    Date.now() - allowCache.ts < ALLOW_TTL_MS
  ) {
    return allowCache.set;
  }
  if (allowInFlight && allowInFlight.includeHip3 === includeHip3) return allowInFlight.p;

  const p = (async (): Promise<Set<string> | null> => {
    try {
      const qs = new URLSearchParams();
      qs.set('includeHip3', includeHip3 ? '1' : '0');
      const { ok, data } = await oroborosJson<unknown>(`/api/available-pairs?${qs}`);
      if (!ok) return allowCache?.includeHip3 === includeHip3 ? allowCache.set : null;
      const symbols = parsePairsPayload(data);
      if (!symbols.length) return allowCache?.includeHip3 === includeHip3 ? allowCache.set : null;
      const set = allowSetFromSymbols(symbols);
      allowCache = { includeHip3, ts: Date.now(), symbols, set };
      return set;
    } catch {
      return allowCache?.includeHip3 === includeHip3 ? allowCache.set : null;
    }
  })().finally(() => {
    if (allowInFlight?.p === p) allowInFlight = null;
  });

  allowInFlight = { includeHip3, p };
  return p;
}

function fallbackMarkets(): OroMarket[] {
  return FALLBACK_SYMBOLS.map((s) => {
    const id = pairToPerpLabel(s);
    return {
      id,
      coin: s,
      symbol: s,
      display: s,
      kind: 'perp' as const,
      tag: 'PERP' as const,
      mid: null,
      changePct: null,
      volume: null,
      openInterest: null,
    };
  });
}

const MARKETS_TTL_MS = 30_000;
const MARKETS_STALE_MS = 5 * 60_000;
let marketsCache: {
  includeHip3: boolean;
  ts: number;
  markets: OroMarket[];
  source: 'hyperliquid' | 'grail' | 'fallback';
} | null = null;
let marketsInFlight: {
  includeHip3: boolean;
  p: Promise<{ markets: OroMarket[]; source: 'hyperliquid' | 'grail' | 'fallback' }>;
} | null = null;

function assembleMarkets(
  tape: OroTapeRow[],
  allow: Set<string> | null,
): { markets: OroMarket[]; source: 'hyperliquid' | 'grail' | 'fallback' } {
  if (!tape.length) {
    return { markets: fallbackMarkets(), source: 'fallback' };
  }
  let markets = tape;
  if (allow && allow.size > 0) {
    const filtered = tape.filter(
      (m) =>
        allow.has(m.id.toUpperCase()) ||
        allow.has(m.coin.toUpperCase()) ||
        allow.has(m.symbol.toUpperCase()) ||
        (m.kind === 'hip3' && allow.has(`${m.display.replace('/', '-')}`)),
    );
    // If allowlist is core-only / stale, keep full HL tape so HIP-3 still appears.
    const hip3InFiltered = filtered.some((m) => m.kind === 'hip3');
    const hip3InTape = tape.some((m) => m.kind === 'hip3');
    markets = hip3InTape && !hip3InFiltered ? tape : filtered.length ? filtered : tape;
  }
  return {
    markets,
    source: allow ? 'grail' : 'hyperliquid',
  };
}

export function peekOroborosMarkets(includeHip3 = true): OroMarket[] | null {
  if (!marketsCache?.markets.length) return null;
  if (includeHip3 && !marketsCache.includeHip3) return null;
  if (Date.now() - marketsCache.ts > MARKETS_STALE_MS) return null;
  return marketsCache.markets;
}

async function loadOroborosMarkets(
  includeHip3: boolean,
): Promise<{ markets: OroMarket[]; source: 'hyperliquid' | 'grail' | 'fallback' }> {
  const [tapeSettled, allow] = await Promise.all([
    fetchOroUniverseTape({ includeHip3 }).catch(() => [] as OroTapeRow[]),
    fetchGrailAllowlist(includeHip3),
  ]);
  const assembled = assembleMarkets(tapeSettled, allow);
  marketsCache = { includeHip3, ts: Date.now(), ...assembled };
  return assembled;
}

export async function fetchOroborosMarkets(opts?: {
  includeHip3?: boolean;
}): Promise<{ markets: OroMarket[]; source: 'hyperliquid' | 'grail' | 'fallback' }> {
  const includeHip3 = opts?.includeHip3 !== false;
  if (
    marketsCache &&
    marketsCache.includeHip3 === includeHip3 &&
    Date.now() - marketsCache.ts < MARKETS_TTL_MS
  ) {
    return { markets: marketsCache.markets, source: marketsCache.source };
  }
  if (
    marketsCache &&
    marketsCache.includeHip3 === includeHip3 &&
    Date.now() - marketsCache.ts < MARKETS_STALE_MS
  ) {
    void refreshOroborosMarkets(includeHip3);
    return { markets: marketsCache.markets, source: marketsCache.source };
  }
  return refreshOroborosMarkets(includeHip3);
}

function refreshOroborosMarkets(
  includeHip3: boolean,
): Promise<{ markets: OroMarket[]; source: 'hyperliquid' | 'grail' | 'fallback' }> {
  if (marketsInFlight && marketsInFlight.includeHip3 === includeHip3) return marketsInFlight.p;
  const p = loadOroborosMarkets(includeHip3).finally(() => {
    if (marketsInFlight?.p === p) marketsInFlight = null;
  });
  marketsInFlight = { includeHip3, p };
  return p;
}

const RECENT_KEY = 'oro_recent_markets';
const FAV_KEY = 'oro_favorite_markets';
const FAV_AT_KEY = 'oro_favorite_markets_at';
export const ORO_FAVORITES_CHANGED_EVENT = 'oro:favorites-changed';

function notifyFavoriteMarketsChanged(next: string[]): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(ORO_FAVORITES_CHANGED_EVENT, { detail: next }));
  } catch {
    /* ignore */
  }
}

export function loadRecentMarkets(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(raw) ? raw.map((x) => String(x)).filter(Boolean).slice(0, 12) : [];
  } catch {
    return [];
  }
}

export function pushRecentMarket(marketId: string): string[] {
  const id = symbolToMarketId(marketId);
  const next = [id, ...loadRecentMarkets().filter((m) => m.toUpperCase() !== id.toUpperCase())].slice(
    0,
    12,
  );
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

export function loadFavoriteMarkets(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(FAV_KEY) || '[]');
    return Array.isArray(raw) ? raw.map((x) => String(x)).filter(Boolean).slice(0, 80) : [];
  } catch {
    return [];
  }
}

export function loadFavoriteMarketsUpdatedAt(): number {
  try {
    const n = Number(localStorage.getItem(FAV_AT_KEY) || '0');
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
}

export function readFavoriteMarketPrefs(): { favorites: string[]; updatedAt: number } {
  return {
    favorites: loadFavoriteMarkets(),
    updatedAt: loadFavoriteMarketsUpdatedAt(),
  };
}

export function replaceFavoriteMarkets(
  marketIds: string[],
  updatedAt: number = Date.now(),
): string[] {
  const seen = new Set<string>();
  const next: string[] = [];
  for (const raw of marketIds) {
    const id = symbolToMarketId(raw);
    const key = id.toUpperCase();
    if (!id || seen.has(key)) continue;
    seen.add(key);
    next.push(id);
    if (next.length >= 80) break;
  }
  const at = Number.isFinite(updatedAt) ? Math.max(0, Math.floor(updatedAt)) : Date.now();
  try {
    localStorage.setItem(FAV_KEY, JSON.stringify(next));
    localStorage.setItem(FAV_AT_KEY, String(at));
  } catch {
    /* ignore */
  }
  notifyFavoriteMarketsChanged(next);
  return next;
}

export function toggleFavoriteMarket(marketId: string): string[] {
  const id = symbolToMarketId(marketId);
  const cur = loadFavoriteMarkets();
  const upper = id.toUpperCase();
  const next = cur.some((m) => m.toUpperCase() === upper)
    ? cur.filter((m) => m.toUpperCase() !== upper)
    : [id, ...cur].slice(0, 80);
  return replaceFavoriteMarkets(next, Date.now());
}
